import { html, unsafeCSS } from 'lit'
import { customElement } from 'lit/decorators.js'

import { translate } from '@/localization/localization'

import accordionStyles from '../accordion/accordion.scss?inline'
import { AccordionBaseElement } from '../accordion/accordion-base-element'
import officialStyles from './official-website-banner.scss?inline'

/**
 * `dap-ds-official-website-banner`
 * @element dap-ds-official-website-banner
 * @summary An official website banner is a banner that displays that the website is an official website.
 * @title - Official website-banner
 *
 * @property { 'sm' | 'lg' } size - The size of the official website banner. Default is `sm`.
 * @property { 1 | 2 | 3 | 4 | 5 | 6 } headingLevel - The heading level of the accordion. Default is `4`.
 * @property {boolean} opened - Whether the accordion is opened. Default is `false`.
 * @property { 'left' | 'right' } iconLocation - The location of the icon. Default is `right`.
 * @property { 'default' | 'collapsed' | 'clean' | 'clean-collapsed' } variant - The variant of the accordion.
 * @property {boolean} lastItem - Whether the accordion is the last item.
 *
 * @event {{ void }} dds-opened - Event fired when the accordion is opened.
 * @event {{ void }} dds-closed - Event fired when the accordion is closed.
 *
 * @slot default - The content of the accordion.
 * @slot heading - The heading of the accordion.
 * @slot icon-opened - The icon when the accordion is opened.
 * @slot icon-closed - The icon when the accordion is closed.
 *
 * @csspart base - The main accordion container.
 * @csspart heading - The heading of the accordion.
 * @csspart button - The button of the accordion.
 * @csspart content - The content of the accordion.
 * @csspart content-container - The container of the accordion content.
 * @csspart icon-wrapper - The icon wrapper of the accordion.
 * @csspart open-icon - The icon when the accordion is opened.
 * @csspart open-icon-base - The base of the icon when the accordion is opened.
 * @csspart close-icon - The icon when the accordion is closed.
 * @csspart close-icon-base - The base of the icon when the accordion is closed.
 *
 * @cssproperty --dds-official-banner-background-color - The background color of the official website banner.
 * @cssproperty --dds-official-banner-text-color - The text color of the official website banner.
 * @cssproperty --dds-official-banner-heading-hover-color - The heading hover color of the official website banner.
 * @cssproperty --dds-official-banner-border-radius - The border radius of the official website banner.
 * @cssproperty --dds-official-banner-icon-color - The icon color of the official website banner.
 * @cssproperty --dds-official-banner-icon-color-hover - The icon hover color of the opener icon official website banner.
 */
@customElement('dap-ds-official-website-banner')
export default class DapDSOfficialWebsiteBanner extends AccordionBaseElement {
  static readonly styles = unsafeCSS(`${accordionStyles}${officialStyles}`)

  headingRender() {
    return html` <span>
      <dap-ds-stack direction="row" spacing="100">
        <dap-ds-icon
          size="xs"
          name="shield-check-fill"
          aria-hidden="true"></dap-ds-icon>
        ${translate('official-website-banner.heading')}
      </dap-ds-stack>
    </span>`
  }

  contentRender() {
    return html` <div
      style="display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1rem;">
      <dap-ds-stack direction="row" spacing="200">
        <dap-ds-icon name="lock-fill" aria-hidden="true"></dap-ds-icon>
        <span style="font-weight: bold"
          >${translate('official-website-banner.heading-2')}</span
        >
      </dap-ds-stack>
      <div style="font-size: var(--dds-font-sm)">
        ${translate('official-website-banner.content')}
      </div>
    </div>`
  }

  render() {
    return html` ${this.renderAccordion()} `
  }
}
